/**
 * ColumnProfileBadge — compact stats badge rendered below each column header
 * when column profiling is enabled. Hover shows a "Click for details" tooltip;
 * click opens a popover with summary stats and a mini chart (histogram for
 * numeric/date columns, top-5 for categorical).
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../components/ui/tooltip';
import type { ColumnProfileStats } from '../utils/columnProfiling';
import { computeColumnHistogram } from '../utils/columnHistogram';
import { computeColumnTopN } from '../utils/columnTopN';
import { MiniHistogram, MiniTopN } from './ColumnProfileMiniCharts';
import {
  categorizeProfileDataType,
  trackDataProfileEvent,
} from '../../data-profile/services/dataProfileAnalytics';

interface ColumnProfileBadgeProps {
  stats: ColumnProfileStats;
  /** Optional: when provided, popover shows histogram (numeric/date) or top-5 (categorical). */
  rows?: Record<string, unknown>[];
  /** Optional: when provided, popover shows an "Open full profile →" footer link. */
  onOpenFullProfile?: () => void;
}

const DATE_TYPE_PREFIXES = ['date', 'datetime', 'timestamp', 'smalldatetime'];

function isDateLikeType(dataType: string | undefined): boolean {
  if (!dataType) return false;
  const base = dataType.toLowerCase().replace(/\(.*\)/, '').trim();
  return DATE_TYPE_PREFIXES.some((p) => base.startsWith(p));
}

function formatStatValue(value: unknown): string {
  if (value === undefined || value === null) return '-';
  if (typeof value === 'number') {
    if (Number.isInteger(value)) return value.toLocaleString();
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  const str = String(value);
  return str.length > 30 ? str.slice(0, 27) + '...' : str;
}

function getNullBarColor(nullPercent: number): string {
  if (nullPercent === 0) return 'bg-emerald-500';
  if (nullPercent === 100) return 'bg-red-500';
  return 'bg-amber-500';
}

export const ColumnProfileBadge = React.memo(function ColumnProfileBadge({
  stats,
  rows,
  onOpenFullProfile,
}: ColumnProfileBadgeProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setIsOpen(next);
      if (next) {
        trackDataProfileEvent('feature:data_profile_popover_open', {
          dataTypeCategory: categorizeProfileDataType(stats.dataType),
        });
      }
    },
    [stats.dataType],
  );

  const filledPercent = 100 - stats.nullPercent;
  const barColor = getNullBarColor(stats.nullPercent);

  const isNumericOrDate = stats.isNumeric || isDateLikeType(stats.dataType);
  const lowDistinct =
    typeof stats.distinctCount === 'number' && stats.distinctCount > 0 && stats.distinctCount <= 5;
  const showTopN = !isNumericOrDate || lowDistinct;

  const histogram = useMemo(() => {
    if (!isOpen || !rows || !isNumericOrDate || showTopN) return null;
    return computeColumnHistogram(rows, stats.columnId, 8, 'equal-width', stats.dataType);
  }, [isOpen, rows, isNumericOrDate, showTopN, stats.columnId, stats.dataType]);

  const topN = useMemo(() => {
    if (!isOpen || !rows || !showTopN) return null;
    return computeColumnTopN(rows, stats.columnId, 5);
  }, [isOpen, rows, showTopN, stats.columnId]);

  if (stats.totalCount === 0) return null;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <div
              className="mt-0.5 h-1.5 w-full rounded-full bg-muted/60 overflow-hidden cursor-pointer"
              data-testid="column-profile-badge"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`h-full rounded-full ${barColor} opacity-70`}
                style={{ width: `${filledPercent}%` }}
              />
            </div>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">Click for details.</TooltipContent>
      </Tooltip>
      <PopoverContent
        side="bottom"
        align="start"
        className="w-[280px] p-3 bg-popover text-popover-foreground border border-border shadow-md"
        data-testid="column-profile-popover"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
          <span className="text-muted-foreground">Total</span>
          <span className="font-medium text-right">{stats.totalCount.toLocaleString()}</span>

          <span className="text-muted-foreground">Nulls</span>
          <span className="font-medium text-right">
            {stats.nullCount.toLocaleString()} ({stats.nullPercent}%)
          </span>

          <span className="text-muted-foreground">Distinct</span>
          <span className="font-medium text-right">
            {typeof stats.distinctCount === 'number'
              ? stats.distinctCount.toLocaleString()
              : stats.distinctCount}
          </span>

          {stats.min !== undefined && (
            <>
              <span className="text-muted-foreground">Min</span>
              <span className="font-medium text-right truncate" title={String(stats.min)}>
                {formatStatValue(stats.min)}
              </span>
            </>
          )}

          {stats.max !== undefined && (
            <>
              <span className="text-muted-foreground">Max</span>
              <span className="font-medium text-right truncate" title={String(stats.max)}>
                {formatStatValue(stats.max)}
              </span>
            </>
          )}

          {stats.avg !== undefined && (
            <>
              <span className="text-muted-foreground">Avg</span>
              <span className="font-medium text-right">{formatStatValue(stats.avg)}</span>
            </>
          )}

          {stats.dataType && (
            <>
              <span className="text-muted-foreground">Type</span>
              <span className="font-medium text-right text-muted-foreground/80">
                {stats.dataType}
              </span>
            </>
          )}
        </div>

        {histogram && histogram.buckets.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border/60">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              Distribution
            </div>
            <MiniHistogram buckets={histogram.buckets} />
          </div>
        )}

        {topN && topN.entries.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border/60">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              Top {topN.entries.length} value{topN.entries.length === 1 ? '' : 's'}
            </div>
            <MiniTopN entries={topN.entries} />
          </div>
        )}

        {onOpenFullProfile && (
          <div className="mt-3 pt-3 border-t border-border/60 text-right">
            <button
              type="button"
              data-testid="column-profile-open-full"
              onClick={() => {
                setIsOpen(false);
                onOpenFullProfile();
              }}
              className="text-xs text-blue-500 hover:text-blue-600 hover:underline"
            >
              Open full profile →
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
});
